import { AfterContentChecked,  ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { filter, take } from 'rxjs';
import { ConfigService } from 'src/shared/services/config.service';
import {  HhsgSharedUserService, HhsgSharedConfigService, XsrfService } from '@hhsg/hhsg-shared-components';
import { PlatformService } from 'src/shared/services/platform.service';
import { Subject, takeUntil } from 'rxjs';
import { UserProfile } from 'src/shared/models/user-profile.model';
import * as workerTimers from 'worker-timers';
import { HapSideNavItemModel } from 'src/shared/models/hap-side-nav.model';
import { SideNavHeaderService } from 'src/shared/services/side-nav-header.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy, OnInit, AfterContentChecked  {
    sideNavItems: HapSideNavItemModel[] = [];
    currentYear: number;
    isSessionAuthenticated: boolean;

    private sessionDuration: number;

    // When the sessions is about to expire set this to true to warn the user.
    sessionExpiring = false;
    sessionSecondsLeft = 0;
    sessionExtensionSeconds = 0;
    sessionMessage = "";
    extendingSession = false;
    previousUrl = "";
    currentUrl = "";
    // Flag to indicate if a token refresh can occur.
    private canRefresh = true;
    private user: UserProfile;
    private _unsubscribe$ = new Subject<void>();

    // These variables track the id of the timer functions being used.
    // One for delay timer on the refresh (only refresh at most once every 30 seconds).
    // One for session idle timer to end session if idle for more than 20 minutes.
    private refreshId: number;
    private idleId: number;  

    constructor(
        public _oidcSecurityService: OidcSecurityService,
        private _eventService: PublicEventsService,
        private _router: Router,
        private _configService: ConfigService,
        private _platformService: PlatformService,
        private _hhsgSharedUserService: HhsgSharedUserService,
        private _hhsgSharedConfigService: HhsgSharedConfigService,
        private _changeDetector: ChangeDetectorRef,
        private _sideNavHeaderService: SideNavHeaderService,
        private _xsrfService: XsrfService) {
        this._router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: any) => {
                this.previousUrl = this.currentUrl;
                this.currentUrl = event.url;
                sessionStorage.setItem('previousUrl', this.previousUrl);
                sessionStorage.setItem('currentUrl', this.currentUrl)

            });
        this.sessionDuration = this._configService.appSettings.idleSessionTimeOut;
    }

    ngAfterContentChecked(): void {
        this._changeDetector.detectChanges();
    }

    ngOnInit(): void {
        this.currentYear = new Date().getUTCFullYear();        

        this._oidcSecurityService.checkAuth().subscribe(({ isAuthenticated, userData, accessToken }) => {
            this.isSessionAuthenticated = isAuthenticated;

            console.log('App Authenticated?', isAuthenticated);
            console.log(`Current access token is '${accessToken}'`);

            // If the session is no longer authenticated (this can happen when the user has been in a child app for awhile)
            // but has a token, then redirect back to login to restart the session.  This will eventually redirect back to this home page
            // after the session has be re-authenticated.
            if (!isAuthenticated && accessToken) {
                this._router.navigateByUrl('/login');
            }
            if(isAuthenticated) {
               this.initXsrf();
            }
            let path = localStorage.getItem("doLogout");
            if (path) {                
                if (path === "true") {
                    this._router.navigateByUrl('/logout');
                }
            }	
        });        

        this._eventService
            .registerForEvents()
            .pipe(filter((notification) => notification.type === EventTypes.CheckSessionReceived))
            .subscribe((value) => console.log('CheckSessionReceived with value from app', value));

        this.runTimer();      

        //get logged-in user roles details
        this._platformService.userProfile$
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe(async (userProfile: UserProfile) => {
                if (userProfile) {
                    let userProfileData: any = {
                        userId: userProfile.userId,
                        email: userProfile.email,
                        firstName: userProfile.firstName,
                        lastName: userProfile.lastName,
                        isSsoUser: !userProfile.isNonSSOUser,
                        brandingImage: userProfile.brandingImage,
                        brandingName: userProfile.brandingName,
                        corporationId: userProfile.corporationId.toString(),
                        roles: userProfile.roles,
                        userApplications: userProfile.userApplications
                    };

                    this._hhsgSharedConfigService.appSettings.applicationName = "Accelerators Portal";

                    //call to setUserProfile method is required to pass the user profile data to shared header component
                    this._hhsgSharedUserService.setUserProfile(userProfileData);

                    this.user = userProfile;
                    this.sessionDuration =this.user.sessionTimeoutMinutes * 60;
                    console.log("Corporation session timeout minutes: " + this.user.sessionTimeoutMinutes);
                    this.sessionSecondsLeft = this.sessionDuration;
                    //Populate left navigation menu options
                    this.sideNavItems = this._sideNavHeaderService.getSideNavHeaders(userProfile.getHapUserRole());
                }
            });
        this.resetSessionSeconds();
    }   

    @HostListener('document:click', ['$event'])
    @HostListener('document:keydown', ['$event'])
    @HostListener('document:wheel', ['$event'])
    async keepAlive(event: any): Promise<void> {
       this.resetSessionSeconds();
    }

    resetSessionSeconds() {
        this.extendingSession = false;
        this.sessionExpiring = false;
        //idle session time out set to 20 minutes, configurable value
        this.sessionSecondsLeft = this.sessionDuration;
        this.sessionExtensionSeconds = 0;
    }

    public extendSession() {
        console.log("Begin refresh...");

        this.extendingSession = true;
        this.canRefresh = false;

        this._oidcSecurityService.getAccessToken().subscribe(token => {
            if (token) {
                this._oidcSecurityService.forceRefreshSession().subscribe(() => {
                    this.resetSessionSeconds();    
                    console.log("Refresh finished...");
                });
            }

        });
       
    }

     // To prevent a bombardment of token refresh requests, this will add a delay (in milliseconds)
    // between token refreshes.
    allowRefresh: Function = () => {
        if (this.refreshId) {
            workerTimers.clearTimeout(this.refreshId);
        }

        this.refreshId = workerTimers.setTimeout(() => { this.canRefresh = true; }, 30000);
    };

    // A recursive function that calls itself every second to decrement
    // the timer and check to see if the session ending modal should be shown.
    runTimer: Function = () => {
        if (this.idleId) {
            workerTimers.clearTimeout(this.idleId);
        }

        this.idleId = workerTimers.setTimeout(async () => {
            if (this.sessionSecondsLeft != this.sessionDuration && this.sessionSecondsLeft % 60 == 0) {
                console.log("Session minutes remaining: " + (this.sessionSecondsLeft / 60).toFixed(0));
            }

            if (this.sessionSecondsLeft > 0 && this.sessionExtensionSeconds < 10) {
                if (this.extendingSession) {
                    this.sessionExtensionSeconds++;
                }
                else {
                    this.sessionSecondsLeft--;
                }
            }
            else {
                this.resetSessionSeconds();
                await this._router.navigateByUrl(`logout`);
            }

            if (this.sessionSecondsLeft <= 60) {
                this.sessionExpiring = true;
            }

            if (this.sessionExpiring) {
                if (this.extendingSession) {
                    let dots = ["", ".", "..", "..."];
                    this.sessionMessage = "Requesting a session extension";
                    this.sessionMessage += dots[this.sessionExtensionSeconds % 4];
                }
                else {
                    this.sessionMessage = "Your session will expire in " + this.sessionSecondsLeft;

                    if (this.sessionSecondsLeft == 1) {
                        this.sessionMessage += " second.";
                    }
                    else {
                        this.sessionMessage += " seconds.";
                    }
                }
            }

            this.runTimer();
        },
            1000);
    }   
    
    ngOnDestroy(): void {
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    initXsrf() {
        this._xsrfService.initXsrf().pipe(take(1)).subscribe();
      }
    
}
